/**
 * A calendar day, as `YYYY-MM-DD`.
 *
 * One module owns the shape, because three copies of it went in three
 * directions: a date-only value has no time and no zone, and every conversion
 * through `Date` is a chance to move it by a day. The endpoint's `scheduledDate`
 * is a Postgres DATE, the report's filter is two calendar days, and the picker
 * hands back a `Date` at local midnight — none of those is an instant.
 *
 * Nothing here is specific to one report, which is why it is not in
 * `autopay-summary-params.ts`: `src/design-system/date-range.tsx` needs the
 * same conversions and must not import a feature's modules.
 */

/** An inclusive range of calendar days, both `YYYY-MM-DD`. */
export interface CalendarDateRange {
  startDate: string
  endDate: string
}

/** The only date shape the report's endpoint accepts. */
export const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * The local calendar day a `Date` falls on, as `YYYY-MM-DD`.
 *
 * Local, not UTC. A picker hands back local midnight, and `toISOString` on that
 * value returns the previous day for every caller west of the meridian.
 *
 * @param value - A date from a picker or a clock.
 * @returns The calendar day.
 */
export function toCalendarDate(value: Date): string {
  // The year is padded like the other two. Unpadded, a year below 1000 emits
  // three digits and the result no longer matches the pattern this module
  // exists to guarantee.
  const year = String(value.getFullYear()).padStart(4, '0')
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * A calendar day as a local-midnight `Date`, or undefined.
 *
 * Local for the same reason `toCalendarDate` is: `new Date('2026-09-03')` is
 * UTC midnight, which a picker draws on the 2nd west of the meridian.
 *
 * @param value - A calendar day.
 * @returns The day at local midnight, or undefined when it is unreadable.
 */
export function fromCalendarDate(value: string): Date | undefined {
  const match = CALENDAR_DATE_PATTERN.exec(value)
  if (!match) {
    return undefined
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const parsed = new Date(year, month - 1, day)
  // `new Date(26, …)` is 1926, not year 26 — the two-digit-year rule, which
  // the four-digit `0026` in the pattern does not escape. `setFullYear` is
  // how a year below 100 is expressed at all.
  parsed.setFullYear(year)

  // `Date` rolls 2026-02-31 forward to March. Without this check the picker
  // would highlight a day in March for a value `calendarDateAtUtcMidnight`
  // calls unreadable — one module saying the day exists and the other saying
  // it does not.
  return parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
    ? parsed
    : undefined
}

/**
 * A calendar day as `MM/DD/YYYY`.
 *
 * Reformatted textually rather than parsed, so no zone can shift it.
 *
 * @param value - A calendar day, or nothing.
 * @returns The rendered day, or the input when it is not a calendar day.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) {
    return ''
  }
  const match = CALENDAR_DATE_PATTERN.exec(value)

  return match ? `${match[2]}/${match[3]}/${match[1]}` : value
}

/**
 * A calendar day anchored at UTC midnight, or NaN.
 *
 * Anchoring at UTC takes the offset out of a subtraction between two days, so
 * a range crossing a daylight-saving boundary is still a whole number of days.
 * The round-trip check rejects a day the pattern admits but the calendar does
 * not, such as `2026-02-31`, which `Date` would silently roll forward.
 *
 * @param value - A calendar day.
 * @returns Milliseconds since the epoch, or NaN.
 */
export function calendarDateAtUtcMidnight(value: string): number {
  if (!CALENDAR_DATE_PATTERN.test(value)) {
    return Number.NaN
  }
  const parsed = Date.parse(`${value}T00:00:00.000Z`)
  if (Number.isNaN(parsed)) {
    return Number.NaN
  }

  return new Date(parsed).toISOString().slice(0, 10) === value
    ? parsed
    : Number.NaN
}

/**
 * Days between two calendar days, counting both endpoints.
 *
 * @param startDate - First day.
 * @param endDate - Last day.
 * @returns The day count, or NaN when either day is unreadable.
 */
export function inclusiveCalendarDays(
  startDate: string,
  endDate: string
): number {
  const start = calendarDateAtUtcMidnight(startDate)
  const end = calendarDateAtUtcMidnight(endDate)

  return (end - start) / MILLISECONDS_PER_DAY + 1
}
